/*
 * Name paints (Skins, mockup 33, ENG-731).
 *
 * A listener's equipped name paint arrives with their chat messages as
 * `namePaint` and with their requests (now playing, up next, the queue) as
 * `requestedByPaint` (backend ENG-732, schema `SkinsNamePaint`):
 *
 *   { skinId, name, rarity, style: 'linear-gradient' | 'solid',
 *     angle: 0-359 (CSS convention), stops: [{ color: '#RRGGBB', position: 0-1 }],
 *     fallbackColor: '#RRGGBB' }
 *
 * The name is drawn from that data alone: no per-user or per-skin CSS. The
 * paint is applied through two custom properties (`--name-paint-image`,
 * `--name-paint-fallback`) that the `.name-paint` class in NamePaint.css turns
 * into gradient-clipped text, with the solid fallback colour where the browser
 * can't clip a background to text.
 *
 * Readable contrast (ENG-484): every colour is lifted toward white until it
 * reaches 4.5:1 (WCAG AA for normal text) against the surface the name sits
 * on, so a dark paint can't vanish into the chat or the now-playing card.
 * Malformed paint data gives null, and the caller draws the standard name.
 */

const HEX = /^#[0-9a-f]{6}$/i;
const MAX_STOPS = 8;

/** WCAG AA for normal-size text. */
export const MIN_NAME_CONTRAST = 4.5;

/**
 * The surfaces a painted name is drawn on, as their darkest solid colour.
 * Chat: the stream chat panel. Now playing: the card's red gradient under
 * the row's 28% black, at its lighter end (the harder case for contrast).
 */
export const NAME_PAINT_SURFACES = {
  chat: '#211B1B',
  nowPlaying: '#660C10',
};

const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

const rgbToHex = (rgb) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

const channel = (v) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of a #RRGGBB colour. */
export const relativeLuminance = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG contrast ratio between two #RRGGBB colours (1 to 21). */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * `color`, mixed toward white just enough to reach `min` against `surface`
 * (the surfaces are dark, so lighter is always the readable direction).
 * Already-readable colours come back unchanged, uppercased.
 */
export const readableOn = (color, surface, min = MIN_NAME_CONTRAST) => {
  const base = hexToRgb(color);
  const mix = (t) => rgbToHex(base.map((v) => v + (255 - v) * t));
  if (contrastRatio(mix(0), surface) >= min) return mix(0);
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 12; i += 1) {
    const mid = (lo + hi) / 2;
    if (contrastRatio(mix(mid), surface) >= min) hi = mid;
    else lo = mid;
  }
  return mix(hi);
};

/**
 * A wire paint checked and tidied, or null when it can't be drawn: unknown
 * style, a colour that isn't #RRGGBB, no stops. Stops are clamped to 0-1 and
 * sorted; the angle is wrapped into 0-359.
 */
export const normalizeNamePaint = (input) => {
  if (!input || typeof input !== 'object') return null;
  const { style, stops, fallbackColor } = input;
  if (style !== 'linear-gradient' && style !== 'solid') return null;
  if (typeof fallbackColor !== 'string' || !HEX.test(fallbackColor)) return null;
  if (!Array.isArray(stops) || stops.length === 0) return null;
  const clean = [];
  for (const stop of stops.slice(0, MAX_STOPS)) {
    if (!stop || typeof stop.color !== 'string' || !HEX.test(stop.color)) return null;
    const position = Number(stop.position);
    clean.push({ color: stop.color.toUpperCase(), position: Number.isFinite(position) ? Math.min(1, Math.max(0, position)) : 0 });
  }
  clean.sort((a, b) => a.position - b.position);
  const angle = Number.isFinite(Number(input.angle)) ? ((Math.round(Number(input.angle)) % 360) + 360) % 360 : 90;
  return {
    skinId: typeof input.skinId === 'string' ? input.skinId : '',
    name: typeof input.name === 'string' ? input.name : '',
    rarity: typeof input.rarity === 'string' ? input.rarity : '',
    style,
    angle,
    stops: clean,
    fallbackColor: fallbackColor.toUpperCase(),
  };
};

/**
 * The inline style that paints a name on `surface` (a #RRGGBB from
 * NAME_PAINT_SURFACES), or null for no paint or a malformed one. Use it with
 * the `name-paint` class from NamePaint.css.
 */
export const namePaintStyle = (paint, surface = NAME_PAINT_SURFACES.chat) => {
  const p = normalizeNamePaint(paint);
  if (!p) return null;
  const fallback = readableOn(p.fallbackColor, surface);
  if (p.style === 'solid' || p.stops.length === 1) {
    const solid = readableOn(p.stops[0].color, surface);
    return { '--name-paint-fallback': solid, '--name-paint-image': `linear-gradient(${solid}, ${solid})` };
  }
  const stops = p.stops
    .map((s) => `${readableOn(s.color, surface)} ${Math.round(s.position * 1000) / 10}%`)
    .join(', ');
  return {
    '--name-paint-fallback': fallback,
    '--name-paint-image': `linear-gradient(${p.angle}deg, ${stops})`,
  };
};
